"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname, useRouter } from "next/navigation";
import { startTransition, useState, useTransition } from "react";

import type { HomeDictionary } from "@/content/home";
import { locales, type Locale } from "@/lib/locales";

interface NavbarProps {
  locale: Locale;
  copy: HomeDictionary["nav"];
}

export default function Navbar({ locale, copy }: NavbarProps) {
  const [isPending, startUiTransition] = useTransition();
  const [open, setOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [query, setQuery] = useState("");
  const pathname = usePathname();
  const router = useRouter();

  const handleSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const searchValue = query.trim().toLowerCase();

    if (!searchValue) {
      return;
    }

    const match = copy.searchSections.find((section) =>
      section.keywords.some((keyword) => searchValue.includes(keyword)),
    );

    router.push(`/${locale}${match?.dest ?? "#contacto"}`);
    setSearchOpen(false);
    setQuery("");
    setOpen(false);
  };

  const handleLocaleSwitch = (targetLocale: Locale) => {
    if (targetLocale === locale) {
      return;
    }

    const hash = window.location.hash;
    const href = `/${targetLocale}${hash}`;
    const navigation = () => {
      startTransition(() => {
        router.push(href);
      });
    };

    if (
      "startViewTransition" in document &&
      !window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      const transitionDocument = document as Document & {
        startViewTransition: (callback: () => void) => { finished: Promise<void> };
      };

      document.documentElement.classList.add("locale-switching");
      transitionDocument
        .startViewTransition(() => {
          startUiTransition(navigation);
        })
        .finished.finally(() => {
          document.documentElement.classList.remove("locale-switching");
        });
    } else {
      navigation();
    }

    setOpen(false);
  };

  const currentHomePath = pathname.startsWith("/en") || pathname.startsWith("/es") ? pathname : `/${locale}`;

  const localeToggle = (
    <div
      className="inline-flex items-center rounded-full border border-white/15 bg-white/5 p-1"
      aria-label="Language selector"
      role="group"
    >
      {locales.map((option) => {
        const isActive = option === locale;

        return (
          <button
            key={option}
            type="button"
            onClick={() => handleLocaleSwitch(option)}
            disabled={isPending || isActive}
            aria-pressed={isActive}
            className={`min-w-10 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${
              isActive
                ? "bg-terracotta text-white shadow-sm"
                : "text-white/75 hover:text-white disabled:opacity-60"
            }`}
          >
            {option.toUpperCase()}
          </button>
        );
      })}
    </div>
  );

  const mobileLocaleToggle = (
    <div
      className="inline-flex items-center rounded-full border border-petroleum/15 bg-white p-1 shadow-sm"
      aria-label="Language selector"
      role="group"
    >
      {locales.map((option) => {
        const isActive = option === locale;

        return (
          <button
            key={option}
            type="button"
            onClick={() => handleLocaleSwitch(option)}
            disabled={isPending || isActive}
            aria-pressed={isActive}
            className={`min-w-10 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] transition-all ${
              isActive
                ? "bg-petroleum text-white"
                : "text-petroleum/70 hover:text-petroleum disabled:opacity-60"
            }`}
          >
            {option.toUpperCase()}
          </button>
        );
      })}
    </div>
  );

  return (
    <header className="fixed top-0 left-0 w-full z-50 font-inter shadow-md bg-white">
      <div className="hidden md:flex w-full h-[40px] bg-petroleum items-center justify-end px-6 lg:px-12 gap-8 text-[11px] uppercase tracking-[0.2em] border-b border-white/10 text-aura font-medium">
        <Link href={`/${locale}#contacto`} className="hover:text-terracotta transition-colors whitespace-nowrap">
          {copy.topContact}
        </Link>
        <span className="text-white/20 select-none">|</span>
        {localeToggle}
      </div>

      <div className="flex w-full h-[80px] md:h-[100px] items-center px-4 lg:px-12">
        <div className="flex-shrink-1 flex-grow-0 w-full max-w-[200px] md:max-w-[260px] lg:max-w-[300px] xl:max-w-[350px] h-full flex items-center">
          <Link href={`/${locale}`} className="relative h-full w-full flex items-center" aria-label="Elite Petroleum">
            <Image
              src="/logo.webp"
              alt="Elite Petroleum Operations"
              width={350}
              height={175}
              priority
              sizes="(max-width: 768px) 140px, (max-width: 1200px) 250px, 350px"
              className="h-auto max-h-[60px] md:max-h-[90px] lg:max-h-[110px] w-auto object-contain object-left"
            />
          </Link>
        </div>

        <div className="flex-grow flex items-center justify-end min-w-0">
          {searchOpen ? (
            <form onSubmit={handleSearch} className="flex-grow max-w-md mx-4">
              <input
                autoFocus
                type="search"
                placeholder={copy.searchPlaceholder}
                className="w-full border-b-2 border-petroleum py-1 text-sm font-bold outline-none text-petroleum bg-transparent"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
            </form>
          ) : (
            <nav className="hidden lg:flex items-center gap-x-4 xl:gap-x-8 mx-4 xl:mx-8">
              {copy.links.map((item) => (
                <Link
                  key={item.id}
                  href={`/${locale}#${item.id}`}
                  className="group relative text-[10px] xl:text-[12px] text-petroleum font-bold uppercase tracking-widest hover:text-terracotta transition-colors font-montserrat whitespace-nowrap"
                >
                  {item.label}
                  <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-terracotta group-hover:w-full transition-all duration-300" />
                </Link>
              ))}
            </nav>
          )}

          <div className="flex items-center gap-2 md:gap-4 flex-shrink-0">
            <button
              onClick={() => setSearchOpen((current) => !current)}
              className="p-2 text-petroleum hover:text-terracotta transition-colors"
              aria-label={copy.searchAria}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </button>

            <Link
              href={`${currentHomePath}#contacto`}
              className="hidden md:block font-bold text-[10px] xl:text-[11px] uppercase border-2 border-terracotta bg-white text-terracotta px-4 xl:px-8 py-3 hover:bg-terracotta hover:text-white transition-all duration-300 font-montserrat shadow-sm whitespace-nowrap"
            >
              {copy.portalCta}
            </Link>

            <div className="md:hidden">
              {mobileLocaleToggle}
            </div>

            <button
              className="lg:hidden p-2 text-petroleum flex-shrink-0"
              onClick={() => setOpen((current) => !current)}
              aria-label={copy.menuAria}
            >
              <svg className="w-7 h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={open ? "M6 18L18 6" : "M4 6h16M4 12h16M4 18h16"} />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <nav className="lg:hidden bg-white border-t p-6 absolute w-full shadow-xl">
          <div className="flex flex-col space-y-4">
            {copy.links.map((item) => (
              <Link
                key={item.id}
                href={`/${locale}#${item.id}`}
                className="font-bold uppercase text-petroleum text-sm font-montserrat"
                onClick={() => setOpen(false)}
              >
                {item.label}
              </Link>
            ))}
            <Link
              href={`/${locale}#contacto`}
              className="font-bold uppercase text-terracotta text-sm font-montserrat"
              onClick={() => setOpen(false)}
            >
              {copy.topContact}
            </Link>
          </div>
        </nav>
      )}
    </header>
  );
}
